import React, { useEffect, useState, useRef, useMemo, Suspense, lazy, Component, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProject } from '../context/ProjectContext';
import {
  listMembers, addMember, updateMemberRole, removeMember, deleteProject,
  searchUsers, ProjectMember, UserSearchResult,
} from '../api/projectsApi';
import { listRegistryTypes } from '../api/metaRegistriesApi';
import Header from '../components/Header';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../context/AuthContext';
import { useBackendStatus } from '../hooks/useBackendApi';
import NavigationRail, { NAV_RAIL_COLLAPSED_WIDTH } from '../components/navigation/NavigationRail';
import type { NavigationRailItem } from '../components/navigation/NavigationRail';
import ProjectGitlabSection from './ProjectGitlabSection';
import FitnessFunctionsSection from './FitnessFunctionsSection';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import SchemaOutlinedIcon from '@mui/icons-material/SchemaOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';

const RegistriesAdminTab = lazy(() => import('./admin/RegistriesAdminTab'));
const RelationsAdminTab = lazy(() => import('./admin/RelationsAdminTab'));
const MetamodelsAdminTab = lazy(() => import('./admin/MetamodelsAdminTab'));
const ClusterConfigAdminTab = lazy(() => import('./admin/ClusterConfigAdminTab'));
const UsersAdminTab = lazy(() => import('./admin/UsersAdminTab'));

const SLUG_ICONS: Record<string, React.ReactNode> = {
  information_systems: <DnsOutlinedIcon fontSize="small" />,
  'business-capabilities': <ExtensionOutlinedIcon fontSize="small" />,
  'bcm-maps': <MapOutlinedIcon fontSize="small" />,
};

const FALLBACK_ICONS: React.ReactNode[] = [
  <CategoryOutlinedIcon fontSize="small" />,
  <LayersOutlinedIcon fontSize="small" />,
  <HubOutlinedIcon fontSize="small" />,
  <SchemaOutlinedIcon fontSize="small" />,
  <ListAltOutlinedIcon fontSize="small" />,
  <WidgetsOutlinedIcon fontSize="small" />,
  <AssignmentOutlinedIcon fontSize="small" />,
  <AccountTreeOutlinedIcon fontSize="small" />,
];

const getRegistryIcon = (slug: string, index = 0): React.ReactNode =>
  SLUG_ICONS[slug] ?? FALLBACK_ICONS[index % FALLBACK_ICONS.length];

const Loader = () => <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={28} /></Box>;

class TabErrorBoundary extends Component<{ children: ReactNode }, { error: string | null }> {
  state = { error: null };
  static getDerivedStateFromError(e: Error) { return { error: e.message }; }
  render() {
    if (this.state.error) {
      return (
        <Box sx={{ p: 2 }}>
          <CircularProgress size={0} sx={{ display: 'none' }} />
          {/* reuse Alert from MUI already in scope via MuiThemeProvider */}
          <div style={{ color: '#ef4444', padding: '12px 16px', border: '1px solid #fca5a5', borderRadius: 6 }}>
            Ошибка загрузки вкладки: {this.state.error}
          </div>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default function ProjectSettingsPage() {
  const { currentProject, projects, refreshProjects, syncProjectFromId, loading: projectLoading } = useProject();
  const navigate = useNavigate();
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const { theme } = useTheme();
  const { hasRole } = useAuth();
  const { usingBackend } = useBackendStatus();
  const [railWidth, setRailWidth] = useState(NAV_RAIL_COLLAPSED_WIDTH);
  const [registryTypes, setRegistryTypes] = useState<{ slug: string; name: string }[]>([
    { slug: 'information_systems', name: 'Информационные системы' },
  ]);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (urlProjectId) syncProjectFromId(urlProjectId);
  }, [urlProjectId, syncProjectFromId, projects]);

  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [newRole, setNewRole] = useState<'member' | 'admin'>('member');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [error, setError] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const projectId = currentProject?.id || urlProjectId;
  const isOwner = currentProject?.role === 'owner';
  const isAdmin = currentProject?.role === 'admin' || isOwner;
  const isGlobalAdmin = hasRole('admin');

  useEffect(() => {
    if (!projectId) return;
    listRegistryTypes(projectId).then(setRegistryTypes).catch(() => {});
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    listMembers(projectId).then(setMembers).catch(console.error);
  }, [projectId]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    searchTimer.current = setTimeout(() => {
      searchUsers(searchQuery).then(setSearchResults).catch(() => setSearchResults([]));
    }, 300);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [searchQuery]);

  const railItems = useMemo((): NavigationRailItem[] => {
    const items: NavigationRailItem[] = [
      {
        id: 'diagrams', label: 'Диаграммы',
        icon: <GridViewOutlinedIcon fontSize="small" />, active: false,
        onClick: () => navigate(`/projects/${encodeURIComponent(projectId!)}`),
      },
    ];
    const dedicated: Record<string, string> = { information_systems: 'systems' };
    const seen = new Set<string>();
    items.push(...registryTypes
      .filter((rt) => { if (seen.has(rt.slug)) return false; seen.add(rt.slug); return true; })
      .map((rt, idx) => {
        const path = rt.slug === 'bcm-maps'
          ? `/projects/${encodeURIComponent(projectId!)}/bcm`
          : dedicated[rt.slug]
          ? `/projects/${encodeURIComponent(projectId!)}/` + dedicated[rt.slug]
          : `/projects/${encodeURIComponent(projectId!)}/registry/${encodeURIComponent(rt.slug)}`;
        return { id: rt.slug, label: rt.name, icon: getRegistryIcon(rt.slug, idx), active: false, onClick: () => navigate(path) };
      }));
    items.push({
      id: 'health-map', label: 'Карта работоспособности',
      icon: <MonitorHeartOutlinedIcon fontSize="small" />, active: false,
      onClick: () => navigate(`/projects/${encodeURIComponent(projectId!)}/health`),
    });
    items.push({
      id: 'settings', label: 'Настройки пространства',
      icon: <SettingsOutlinedIcon fontSize="small" />, active: true, onClick: () => {},
    });
    return items;
  }, [registryTypes, projectId, navigate]);

  const muiTheme = useMemo(() => createTheme({
    palette: {
      mode: theme.name === 'dark' ? 'dark' : 'light',
      background: { default: theme.background, paper: theme.codePanel.background },
      text: { primary: theme.toolbar.text, secondary: theme.name === 'dark' ? '#9ca3af' : '#6b7280' },
      divider: theme.toolbar.buttonBorder,
      primary: { main: theme.name === 'dark' ? '#3b82f6' : '#2563eb' },
      error: { main: '#ef4444' },
      success: { main: '#22c55e' },
    },
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: theme.toolbar.buttonBorder, color: theme.toolbar.text },
          head: { fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.7, background: theme.toolbar.buttonBackgroundActive },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { background: theme.name === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' },
          },
        },
      },
      MuiOutlinedInput: { styleOverrides: { notchedOutline: { borderColor: theme.toolbar.buttonBorder } } },
      MuiDivider: { styleOverrides: { root: { borderColor: theme.toolbar.buttonBorder } } },
      MuiButton: { styleOverrides: { root: { textTransform: 'none' } } },
      MuiTab: { styleOverrides: { root: { textTransform: 'none' } } },
    },
  }), [theme]);

  const TABS = useMemo(() => [
    { label: 'Участники', show: true },
    { label: 'Реестры', show: isAdmin },
    { label: 'Связи', show: isAdmin },
    { label: 'Метамодели', show: isAdmin },
    { label: 'GitLab', show: isAdmin },
    { label: 'K8s кластеры', show: isAdmin },
    { label: 'Fitness Functions', show: isOwner },
    { label: 'Пользователи', show: isGlobalAdmin && usingBackend },
    { label: 'Опасная зона', show: isOwner },
  ].filter(t => t.show), [isAdmin, isOwner, isGlobalAdmin, usingBackend]);

  if (projectLoading) {
    return (
      <>
        <Header title="Настройки" height={56} />
        <div style={{ padding: 24, paddingTop: 88, background: theme.background, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </div>
      </>
    );
  }

  if (!currentProject || !isAdmin) {
    return (
      <>
        <Header title="Настройки" height={56} />
        <div style={{ padding: 24, paddingTop: 88, background: theme.background, minHeight: '100vh', color: theme.toolbar.text }}>
          Недостаточно прав
        </div>
      </>
    );
  }

  const reload = () => listMembers(projectId!).then(setMembers);

  const handleAdd = async () => {
    if (!selectedUser) return;
    try {
      await addMember(projectId!, selectedUser.id, newRole);
      setAddOpen(false); setSearchQuery(''); setSelectedUser(null); setSearchResults([]);
      reload();
    } catch (e: any) { setError(e.message); }
  };

  const handleRoleChange = async (userId: string, role: string) => {
    try { await updateMemberRole(projectId!, userId, role); reload(); }
    catch (e: any) { setError(e.message); }
  };

  const handleRemove = async (userId: string) => {
    try { await removeMember(projectId!, userId); reload(); }
    catch (e: any) { setError(e.message); }
  };

  const handleDeleteProject = async () => {
    try { await deleteProject(projectId!); await refreshProjects(); navigate('/'); }
    catch (e: any) { setError(e.message); }
  };

  const closeAdd = () => { setAddOpen(false); setSearchQuery(''); setSelectedUser(null); setSearchResults([]); };

  const safeTab = Math.min(tab, TABS.length - 1);
  const currentTabLabel = TABS[safeTab]?.label;

  const cell: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: `1px solid ${theme.toolbar.buttonBorder}`,
    color: theme.toolbar.text,
    fontSize: 14,
  };

  const headCell: React.CSSProperties = {
    ...cell,
    fontWeight: 600,
    opacity: 0.7,
    fontSize: 12,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 6,
    border: `1px solid ${theme.toolbar.buttonBorder}`,
    background: theme.toolbar.buttonBackground,
    color: theme.toolbar.text,
    outline: 'none',
    fontSize: 14,
    boxSizing: 'border-box',
  };

  return (
    <>
      <Header title={`${currentProject.name} — Настройки`} height={56} />
      <NavigationRail
        ariaLabel="Реестры пространства"
        theme={theme}
        items={railItems}
        onExpandedChange={setRailWidth}
      />
      <div style={{ padding: 24, paddingTop: 88, paddingLeft: railWidth + 24, background: theme.background, minHeight: '100vh', transition: 'padding-left .16s ease' }}>
        <h1 style={{ color: theme.toolbar.text, margin: '0 0 20px', fontSize: 22, fontWeight: 700 }}>
          Настройки пространства
        </h1>

        <MuiThemeProvider theme={muiTheme}>
          <Tabs
            value={safeTab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderBottom: `1px solid ${theme.toolbar.buttonBorder}`,
              mb: 3,
              '.MuiTab-root': { color: theme.toolbar.text, opacity: 0.6, textTransform: 'none', fontSize: 14 },
              '.Mui-selected': { color: theme.toolbar.text, opacity: 1 },
              '.MuiTabs-indicator': { backgroundColor: theme.name === 'dark' ? '#3b82f6' : '#2563eb' },
            }}
          >
            {TABS.map(t => <Tab key={t.label} label={t.label} />)}
          </Tabs>

          {error && (
            <div style={{ color: '#ef4444', marginBottom: 16, padding: '10px 14px', border: '1px solid #fca5a5', borderRadius: 6, background: theme.name === 'dark' ? '#2d1515' : '#fff5f5' }}>
              {error}
            </div>
          )}

          <TabErrorBoundary>
          <Suspense fallback={<Loader />}>
            {/* Участники */}
            {currentTabLabel === 'Участники' && (
              <section>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                  <Button variant="outlined" size="small" onClick={() => setAddOpen(true)}>Добавить</Button>
                </div>
                <div style={{ border: `1px solid ${theme.toolbar.buttonBorder}`, borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px auto', background: theme.toolbar.buttonBackgroundActive }}>
                    <div style={headCell}>Пользователь</div>
                    <div style={headCell}>Email</div>
                    <div style={headCell}>Роль</div>
                    <div style={headCell} />
                  </div>
                  {members.length === 0 && (
                    <div style={{ ...cell, opacity: 0.5, textAlign: 'center', padding: 20 }}>Нет участников</div>
                  )}
                  {members.map((m) => (
                    <div key={m.user_id} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 120px auto', alignItems: 'center' }}>
                      <div style={cell}>{m.display_name || m.username}</div>
                      <div style={{ ...cell, opacity: 0.7, fontSize: 13 }}>{m.email}</div>
                      <div style={cell}>
                        {isOwner && m.role !== 'owner' ? (
                          <Select value={m.role} size="small" onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                            sx={{ fontSize: 13, color: theme.toolbar.text, '.MuiOutlinedInput-notchedOutline': { borderColor: theme.toolbar.buttonBorder }, '.MuiSvgIcon-root': { color: theme.toolbar.text } }}>
                            <MenuItem value="member">member</MenuItem>
                            <MenuItem value="admin">admin</MenuItem>
                            <MenuItem value="owner">owner (передать)</MenuItem>
                          </Select>
                        ) : (
                          <span style={{ fontSize: 13, opacity: 0.8 }}>{m.role}</span>
                        )}
                      </div>
                      <div style={{ ...cell, textAlign: 'right' }}>
                        {m.role !== 'owner' && !(isAdmin && !isOwner && m.role === 'admin') && (
                          <Button size="small" color="error" onClick={() => handleRemove(m.user_id)}>Удалить</Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {currentTabLabel === 'Реестры' && <RegistriesAdminTab />}
            {currentTabLabel === 'Связи' && <RelationsAdminTab />}
            {currentTabLabel === 'Метамодели' && <MetamodelsAdminTab />}
            {currentTabLabel === 'GitLab' && <ProjectGitlabSection projectId={projectId!} />}
            {currentTabLabel === 'K8s кластеры' && <ClusterConfigAdminTab />}
            {currentTabLabel === 'Fitness Functions' && <FitnessFunctionsSection projectId={projectId!} />}
            {currentTabLabel === 'Пользователи' && <UsersAdminTab />}

            {/* Опасная зона */}
            {currentTabLabel === 'Опасная зона' && (
              <section style={{ maxWidth: 560, padding: 20, border: '1px solid #fca5a5', borderRadius: 8, background: theme.name === 'dark' ? '#2d1515' : '#fff5f5' }}>
                <h3 style={{ color: '#ef4444', marginTop: 0, marginBottom: 8, fontSize: 15 }}>Опасная зона</h3>
                <p style={{ color: theme.toolbar.text, opacity: 0.7, margin: '0 0 16px', fontSize: 14 }}>
                  Удаление пространства необратимо. Все диаграммы и реестры будут потеряны.
                </p>
                <Button variant="outlined" color="error" onClick={() => setDeleteOpen(true)}>Удалить пространство</Button>
              </section>
            )}
          </Suspense>
          </TabErrorBoundary>
        </MuiThemeProvider>
      </div>

      {/* Add member dialog */}
      <Dialog open={addOpen} onClose={closeAdd} maxWidth="sm" fullWidth>
        <DialogTitle>Добавить участника</DialogTitle>
        <DialogContent>
          <div style={{ marginTop: 8, marginBottom: 12 }}>
            <input
              style={inputStyle}
              placeholder="Поиск по имени или email"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setSelectedUser(null); }}
              autoFocus
            />
            {searchResults.length > 0 && (
              <div style={{ border: `1px solid ${theme.toolbar.buttonBorder}`, borderRadius: 6, marginTop: 4, maxHeight: 200, overflowY: 'auto', background: theme.codePanel.background }}>
                {searchResults.map((u) => (
                  <div key={u.id}
                    onClick={() => { setSelectedUser(u); setSearchQuery(u.username); setSearchResults([]); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderRadius: 4, background: selectedUser?.id === u.id ? (theme.name === 'dark' ? '#1e3a5f' : '#e8f0fe') : 'transparent', color: theme.toolbar.text }}>
                    <span style={{ fontWeight: 500 }}>{u.username}</span>
                    {u.email && <span style={{ opacity: 0.6, marginLeft: 8, fontSize: 13 }}>{u.email}</span>}
                  </div>
                ))}
              </div>
            )}
            {selectedUser && (
              <div style={{ marginTop: 8, fontSize: 13, color: theme.toolbar.text, opacity: 0.8 }}>
                Выбран: <strong>{selectedUser.username}</strong> ({selectedUser.email})
              </div>
            )}
          </div>
          <Select value={newRole} onChange={(e) => setNewRole(e.target.value as any)} fullWidth size="small">
            <MenuItem value="member">member</MenuItem>
            <MenuItem value="admin">admin</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd}>Отмена</Button>
          <Button onClick={handleAdd} variant="contained" disabled={!selectedUser}>Добавить</Button>
        </DialogActions>
      </Dialog>

      {/* Delete project dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Удалить пространство «{currentProject.name}»?</DialogTitle>
        <DialogContent>
          <div style={{ color: theme.toolbar.text, opacity: 0.8 }}>Это действие необратимо.</div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Отмена</Button>
          <Button onClick={handleDeleteProject} color="error" variant="contained">Удалить</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
